import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { TooltipDefinition } from '@/data/tooltipDefinitions';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MicroTooltipProps {
  tooltip: TooltipDefinition;
  anchorElement: HTMLElement;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
}

interface Position {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

export const MicroTooltip = ({
  tooltip,
  anchorElement,
  currentIndex,
  totalSteps,
  onNext,
  onDismiss,
}: MicroTooltipProps) => {
  const [position, setPosition] = useState<Position | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const Icon = tooltip.icon;
  const isLastStep = currentIndex === totalSteps - 1;

  // Calculate position based on anchor and placement
  // NOTE: No defensive null checks needed here - anchor lifecycle is managed by
  // TooltipAnchorContext registry. See docs/invariants/LIFECYCLE_OWNERSHIP.md
  useEffect(() => {
    if (!anchorElement || !tooltipRef.current) return;

    // Auto-scroll anchor into view if it's below viewport
    const anchorRect = anchorElement.getBoundingClientRect();
    if (anchorRect.top > window.innerHeight * 0.7 || anchorRect.bottom < 0) {
      anchorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const updatePosition = () => {
      const anchorRect = anchorElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current!.getBoundingClientRect();
      const padding = 12;
      const isMobile = window.innerWidth < 768;

      let top = 0;
      let left = 0;
      let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

      // On mobile, force bottom placement for right-side tooltips only
      const effectivePlacement = isMobile && tooltip.placement === 'right'
        ? 'bottom'
        : tooltip.placement;

      switch (effectivePlacement) {
        case 'bottom':
          top = anchorRect.bottom + padding;
          left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
          arrowPosition = 'top';
          break;
        case 'top':
          top = anchorRect.top - tooltipRect.height - padding;
          left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
          arrowPosition = 'bottom';
          break;
        case 'left':
          top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
          left = anchorRect.left - tooltipRect.width - padding;
          arrowPosition = 'right';
          break;
        case 'right':
          top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
          left = anchorRect.right + padding;
          arrowPosition = 'left';
          break;
      }

      // Keep tooltip within viewport
      const viewportPadding = 16;
      left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));
      top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding));

      setPosition({ top, left, arrowPosition });
    };

    // Initial position + slight delay for accurate measurement
    requestAnimationFrame(updatePosition);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorElement, tooltip.placement]);

  // Arrow styles based on position
  const arrowClasses = {
    top: 'absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-card',
    bottom: 'absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-card',
    left: 'absolute -left-2 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-card',
    right: 'absolute -right-2 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[8px] border-l-card',
  };

  return (
    <div
      ref={tooltipRef}
      className={cn(
        "fixed z-[9999] w-72 bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-xl p-4 animate-fade-in",
        !position && "opacity-0"
      )}
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      {/* Arrow */}
      {position && <div className={arrowClasses[position.arrowPosition]} />}

      {/* Content */}
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h4 className="text-sm font-semibold text-foreground">{tooltip.title}</h4>
        </div>

        {/* Body */}
        <p className="text-xs text-muted-foreground leading-relaxed">
          {tooltip.body}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border/30">
          <span className="text-[10px] text-muted-foreground">
            {currentIndex + 1} of {totalSteps}
          </span>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground"
            >
              Got it
            </Button>
            <Button
              size="sm"
              onClick={onNext}
              className="h-7 px-4 text-xs"
            >
              {isLastStep ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
